/**
 * Sesión de ESPECTADOR (solo lectura) de una sala en vivo.
 *
 * No predice, no manda inputs y no ocupa slot: se conecta con `?spectate=1`,
 * recibe los mismos snapshots que los jugadores y dibuja lo que dicen,
 * interpolando entre los dos últimos para que se vea fluido a cualquier
 * refresco. Es deliberadamente tonta: un espectador no tiene nada que
 * reconciliar, así que meterle el `PredictiveClient` (que corrige la
 * predicción de un input que nunca envía) sería pagar complejidad para
 * empeorar la imagen.
 *
 * Vive en el cliente porque usa el `WebSocket` del navegador (no puede ir en
 * `@tak/sim`, que se compila sin DOM).
 */
import {
  GameState, MatchRules, RoomPhase, Snapshot, createGame, mapById,
  decodeServer, setActiveSlots,
} from "@tak/sim";

/** Snapshots que se guardan para interpolar (≈0,5 s a 20 Hz). */
const BUFFER = 10;
/** Ticks de retraso al dibujar: colchón para el jitter de la red. */
const DELAY_TICKS = 4;
const TICK_RATE = 60;

export class SpectatorSession {
  /** Estado que se dibuja. Se recrea si la sala cambia de cancha. */
  game: GameState;
  private ws: WebSocket | null = null;
  private readonly buf: Snapshot[] = [];
  private renderTick = -1;
  private lastMessageAt = 0;
  private closed = false;
  private mapId = "classic";

  /** Fase de la sala espectada (para saber si de verdad se está jugando). */
  phase: RoomPhase = "playing";
  /** Nombres de equipo de la sala (los difunde el servidor con el estado). */
  teamNames: [string, string] = ["ROJO", "TURQUESA"];
  /** Se llama cuando la conexión se cae o el servidor la rechaza. */
  onEnd: (() => void) | null = null;

  constructor(private readonly url: string) {
    this.game = createGame(mapById(this.mapId), { goalLimit: 0, timeLimit: 0 }, { players: 2 });
    this.connect();
  }

  /** ¿Está llegando de verdad estado de la sala? (si no, no hay nada que ver). */
  get live(): boolean {
    return !this.closed && this.buf.length > 0 && performance.now() - this.lastMessageAt < 5000;
  }

  private connect(): void {
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.end();
      return;
    }
    this.ws = ws;
    ws.onmessage = (ev: MessageEvent): void => {
      const msg = decodeServer(typeof ev.data === "string" ? ev.data : "");
      if (!msg) return;
      this.lastMessageAt = performance.now();
      if (msg.t === "welcome") {
        this.reset(msg.mapId, msg.goalLimit, msg.timeLimit, msg.players);
      } else if (msg.t === "state") {
        this.phase = msg.phase;
        if (msg.teamNames) this.teamNames = [msg.teamNames[0], msg.teamNames[1]];
        // Un cambio de cancha recrea el mundo: los cuerpos de una cancha no
        // valen para otra (paredes y porterías están en otro sitio).
        if (msg.mapId && msg.mapId !== this.mapId) {
          this.reset(msg.mapId, msg.goalLimit, msg.timeLimit, this.game.players.length);
        }
      } else if (msg.t === "snapshot") {
        this.push(msg.snap);
      }
    };
    ws.onclose = () => this.end();
    ws.onerror = () => { /* el cierre llega detrás; no hay nada que reintentar */ };
  }

  private reset(mapId?: string, goalLimit?: number, timeLimit?: number, players?: number): void {
    this.mapId = mapId ?? this.mapId;
    const rules: MatchRules = { goalLimit: goalLimit ?? 0, timeLimit: timeLimit ?? 0 };
    this.game = createGame(mapById(this.mapId), rules, { players: Math.max(2, players ?? 2) });
    this.buf.length = 0;
    this.renderTick = -1;
  }

  private push(snap: Snapshot): void {
    // Snapshot de una sala con más slots de los que tiene este mundo (llegó
    // antes que el welcome, o la sala se reconfiguró): se recrea a su medida.
    if (snap.players.length !== this.game.players.length) {
      this.reset(this.mapId, this.game.rules.goalLimit, this.game.rules.timeLimit, snap.players.length);
    }
    if (this.buf.length > 0 && snap.serverTick <= this.buf[this.buf.length - 1].serverTick) return;
    this.buf.push(snap);
    while (this.buf.length > BUFFER) this.buf.shift();
    if (snap.phase) this.phase = snap.phase;
    if (this.renderTick < 0) this.renderTick = snap.serverTick - DELAY_TICKS;
  }

  /** Avanza el reloj de interpolación y escribe las posiciones a dibujar. */
  update(dt: number): void {
    if (this.buf.length === 0) return;
    const latest = this.buf[this.buf.length - 1];
    const target = latest.serverTick - DELAY_TICKS;
    this.renderTick += dt * TICK_RATE;
    // Si nos hemos quedado atrás (pestaña en segundo plano) o adelantados
    // (pausa del servidor), se engancha al objetivo en vez de acumular deriva.
    if (Math.abs(target - this.renderTick) > 30) this.renderTick = target;
    else this.renderTick += (target - this.renderTick) * 0.05;

    const [a, b, t] = this.bracket(this.renderTick);
    const g = this.game;
    const w = g.world;
    if (a.active) setActiveSlots(g, a.active);
    for (let i = 0; i < g.players.length; i++) {
      const body = g.players[i];
      const pa = a.players[i];
      const pb = b.players[i] ?? pa;
      if (body < 0 || !pa) continue;
      w.px[body] = pa.x + (pb.x - pa.x) * t;
      w.py[body] = pa.y + (pb.y - pa.y) * t;
    }
    w.px[g.ball] = a.ball.x + (b.ball.x - a.ball.x) * t;
    w.py[g.ball] = a.ball.y + (b.ball.y - a.ball.y) * t;
    g.score[0] = a.score[0];
    g.score[1] = a.score[1];
    g.matchTime = a.matchTime ?? g.matchTime;
    g.goalAnim = 0; // el espectador no reproduce la animación de gol del HUD
  }

  /** Los dos snapshots que rodean a `tick` y la fracción entre ellos. */
  private bracket(tick: number): [Snapshot, Snapshot, number] {
    const buf = this.buf;
    if (tick <= buf[0].serverTick) return [buf[0], buf[0], 0];
    for (let i = 0; i < buf.length - 1; i++) {
      const a = buf[i];
      const b = buf[i + 1];
      if (tick >= a.serverTick && tick <= b.serverTick) {
        const span = b.serverTick - a.serverTick;
        return [a, b, span > 0 ? (tick - a.serverTick) / span : 0];
      }
    }
    const last = buf[buf.length - 1];
    return [last, last, 0];
  }

  private end(): void {
    if (this.closed) return;
    this.closed = true;
    this.onEnd?.();
  }

  close(): void {
    this.closed = true;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try { ws.close(); } catch { /* ya cerrado */ }
    }
  }
}
